import { AgreoRealTime, Lot, TruckAvailabilityDto } from '@agroone/entities'

/**
 * Business rules driven by the lot lifecycle (harvest and transport monitoring).
 *
 * A lot goes through: planned -> harvesting -> in field -> arriving -> at factory -> out of factory.
 * Every step has a theoretical time (planned) and a real time (encoded live); the real time
 * always takes precedence over the theoretical one.
 */

/** A truck is considered late when its theoretical factory arrival is exceeded by this delay. */
export const LATE_THRESHOLD_MINUTES = 10

/** A harvest running (or planned to run) longer than this raises an alert on the site. */
export const HARVEST_DURATION_ALERT_HOURS = 4

const MINUTE_MS = 60 * 1000
const LATE_THRESHOLD_MS = LATE_THRESHOLD_MINUTES * MINUTE_MS
const HARVEST_ALERT_MS = HARVEST_DURATION_ALERT_HOURS * 60 * MINUTE_MS

/** Progress status of a lot, ordered from the least to the most advanced. */
export enum LotProgressStatus {
  PLANNED = 'PLANNED',
  HARVESTING = 'HARVESTING',
  IN_FIELD = 'IN_FIELD',
  LATE = 'LATE',
  ARRIVING = 'ARRIVING',
  AT_FACTORY = 'AT_FACTORY',
  OUT_OF_FACTORY = 'OUT_OF_FACTORY',
}

/** Alerts a lot can raise on the harvest-monitoring screens. */
export enum LotAlertType {
  /** Harvest started more than 4 hours ago and still not finished. */
  HARVEST_RUNNING_TOO_LONG = 'HARVEST_RUNNING_TOO_LONG',
  /** Harvest not started yet but its theoretical duration exceeds 4 hours. */
  THEORETICAL_HARVEST_TOO_LONG = 'THEORETICAL_HARVEST_TOO_LONG',
  /** Truck left the field and its theoretical factory arrival is exceeded by 10 minutes. */
  FACTORY_ARRIVAL_LATE = 'FACTORY_ARRIVAL_LATE',
  /** Truck is at the factory and its theoretical factory leave is exceeded by 10 minutes. */
  FACTORY_LEAVE_LATE = 'FACTORY_LEAVE_LATE',
}

const asTime = (date: Date | string | null | undefined): number | null => {
  if (date == null) {
    return null
  }
  const time = new Date(date).getTime()
  return Number.isNaN(time) ? null : time
}

/** Real time when encoded, theoretical otherwise. */
const realOrTheorical = (real: Date | string | null | undefined, theorical: Date | string | null | undefined) =>
  asTime(real) ?? asTime(theorical)

export const LotRules = {
  /**
   * Determines the progress status of a lot from its encoded real times:
   *
   * 1. factory leave filled                                    -> OUT_OF_FACTORY
   * 2. factory arrival filled                                  -> AT_FACTORY
   * 3. field leave filled + theoretical arrival + 10 min past  -> LATE
   * 4. field leave filled                                      -> ARRIVING (on the road)
   * 5. field arrival filled                                    -> IN_FIELD
   * 6. harvest start filled                                    -> HARVESTING
   * 7. no real time filled                                     -> PLANNED
   */
  getProgressStatus: (lot: Lot, now: Date = new Date()): LotProgressStatus => {
    if (lot.truckFactoryLeaveReal) {
      return LotProgressStatus.OUT_OF_FACTORY
    }
    if (lot.truckFactoryArrivalReal) {
      return LotProgressStatus.AT_FACTORY
    }
    const arrivalTheorical = asTime(lot.truckFactoryArrivalTheorical)
    if (lot.truckFieldLeaveReal && arrivalTheorical != null && now.getTime() > arrivalTheorical + LATE_THRESHOLD_MS) {
      return LotProgressStatus.LATE
    }
    if (lot.truckFieldLeaveReal) {
      return LotProgressStatus.ARRIVING
    }
    if (lot.truckFieldArrivalReal) {
      return LotProgressStatus.IN_FIELD
    }
    if (lot.startHarvestDateReal) {
      return LotProgressStatus.HARVESTING
    }
    return LotProgressStatus.PLANNED
  },

  /**
   * Alerts raised by a lot on the monitoring screens. Lots whose theoretical activity ends
   * before `firstDisplayedDate` are out of the monitored window and never alert.
   */
  getAlerts: (lot: Lot, firstDisplayedDate: Date, now: Date = new Date()): LotAlertType[] => {
    const alerts: LotAlertType[] = []
    const windowStart = firstDisplayedDate.getTime()
    const nowTime = now.getTime()

    const startReal = asTime(lot.startHarvestDateReal)
    const endReal = asTime(lot.endHarvestDateReal)
    const startTheorical = asTime(lot.startHarvestDateTheorical)
    const endTheorical = asTime(lot.endHarvestDateTheorical)

    if (startReal != null && endReal == null && endTheorical != null && endTheorical > windowStart) {
      if (nowTime - startReal > HARVEST_ALERT_MS) {
        alerts.push(LotAlertType.HARVEST_RUNNING_TOO_LONG)
      }
    }

    if (
      startTheorical != null &&
      endTheorical != null &&
      startReal == null &&
      endReal == null &&
      endTheorical > windowStart &&
      endTheorical - startTheorical > HARVEST_ALERT_MS
    ) {
      alerts.push(LotAlertType.THEORETICAL_HARVEST_TOO_LONG)
    }

    const fieldArrivalTheorical = asTime(lot.truckFieldArrivalTheorical)
    const factoryArrivalTheorical = asTime(lot.truckFactoryArrivalTheorical)
    if (
      lot.truckFieldLeaveReal &&
      !lot.truckFactoryArrivalReal &&
      fieldArrivalTheorical != null &&
      fieldArrivalTheorical > windowStart &&
      factoryArrivalTheorical != null &&
      nowTime - factoryArrivalTheorical > LATE_THRESHOLD_MS
    ) {
      alerts.push(LotAlertType.FACTORY_ARRIVAL_LATE)
    }

    const factoryLeaveTheorical = asTime(lot.truckFactoryLeaveTheorical)
    if (
      lot.truckFactoryArrivalReal &&
      !lot.truckFactoryLeaveReal &&
      factoryLeaveTheorical != null &&
      factoryLeaveTheorical > windowStart &&
      nowTime - factoryLeaveTheorical > LATE_THRESHOLD_MS
    ) {
      alerts.push(LotAlertType.FACTORY_LEAVE_LATE)
    }

    return alerts
  },

  /**
   * Minimum start date allowed for a lot:
   * - first lot of the offer -> end of the previous offer (fallback provided by the caller)
   * - other lots             -> end (real, else theoretical) of the previous lot
   */
  getMinStartDate: (allLots: Lot[], lot: Lot, firstLotFallback: Date | null | undefined): Date | null => {
    const index = allLots.findIndex((l) => l.id === lot.id)
    if (index > 0) {
      const previous = allLots[index - 1]
      const end = realOrTheorical(previous.endHarvestDateReal, previous.endHarvestDateTheorical)
      if (end != null) {
        const minDate = new Date(end)
        minDate.setSeconds(0)
        return minDate
      }
    }
    return firstLotFallback ? new Date(firstLotFallback) : null
  },

  /**
   * Maximum end date allowed for a lot:
   * - last lot of the offer -> real start of the first lot of the next offer, minus the
   *   travel duration between the two offers (there must be room to move the harvest team)
   * - other lots            -> real start of the next lot that has one
   */
  getMaxEndDate: (
    allLots: Lot[],
    lot: Lot,
    nextOffer?: {
      firstLotStartReal?: Date | string | null
      /** Travel time between the two offers, in minutes (nextOffer.start - offer.end). */
      displacementMinutes?: number
    } | null,
  ): Date | null => {
    const index = allLots.findIndex((l) => l.id === lot.id)
    if (index === allLots.length - 1) {
      const nextStart = asTime(nextOffer?.firstLotStartReal)
      if (nextStart == null) {
        return null
      }
      return new Date(nextStart - (nextOffer?.displacementMinutes ?? 0) * MINUTE_MS)
    }
    for (let i = index + 1; i < allLots.length; i++) {
      const nextRealStart = asTime(allLots[i].startHarvestDateReal)
      if (nextRealStart != null) {
        return new Date(nextRealStart)
      }
    }
    return null
  },

  /**
   * Duration (seconds) of the "initial break" of a lot: the gap between the theoretical and
   * the real harvest start when the harvest started late. This break has no database row;
   * only its type and comment are persisted, on the lot itself.
   */
  getInitialBreakSeconds: (lot: Lot): number => {
    const startReal = asTime(lot.startHarvestDateReal)
    const startTheorical = asTime(lot.startHarvestDateTheorical)
    if (startReal == null || startTheorical == null || startReal <= startTheorical) {
      return 0
    }
    return Math.floor((startReal - startTheorical) / 1000)
  },

  /**
   * Whether the Agreo real-time factory check-in should be suggested for this lot: the lot
   * has a truck, Agreo saw it enter the factory (and not leave yet), the real arrival is not
   * encoded and the user has not dismissed the suggestion.
   */
  shouldSuggestAgreoFactoryArrival: (lot: Lot, agreo: AgreoRealTime | null | undefined): boolean =>
    !!lot.truck &&
    !lot.ignoreAgreoTruckArrivalReal &&
    !!agreo?.checkInFactory &&
    !agreo?.checkOutFactory &&
    !lot.truckFactoryArrivalReal,

  /**
   * Whether the Agreo real-time factory check-out (and weight) should be suggested: the lot
   * has a truck, Agreo saw it leave with a weight, the real leave is not encoded and at least
   * one of the two suggestions (leave time, weight) has not been dismissed.
   */
  shouldSuggestAgreoFactoryLeave: (lot: Lot, agreo: AgreoRealTime | null | undefined): boolean =>
    !!lot.truck &&
    (!lot.ignoreAgreoTruckLeaveReal || !lot.ignoreAgreoTruckLeaveWeight) &&
    !!agreo?.checkOutFactory &&
    !!agreo?.truckWeightLeave &&
    !lot.truckFactoryLeaveReal,

  /**
   * Whether the Agreo lot number should be suggested: the lot has a truck, Agreo knows the
   * number, none is encoded yet, the suggestion was not dismissed and there are Agreo lot
   * numbers available to pick from.
   */
  shouldSuggestAgreoLotNumber: (lot: Lot, agreo: AgreoRealTime | null | undefined, agreoLotCount: number): boolean =>
    !!lot.truck && !lot.ignoreAgreoLotNumber && !!agreo?.lotNumber && !lot.agreoLotId && agreoLotCount > 0,

  /**
   * A truck is possibly unavailable for a lot when its last known usage ends after the
   * planned field arrival of the lot (it would still be busy elsewhere).
   */
  isTruckPossiblyUnavailable: (availability: TruckAvailabilityDto | undefined, lot: Lot): boolean => {
    if (!availability) {
      return false
    }
    const lastUsage = asTime(availability.lastUsageDate)
    const fieldArrival = realOrTheorical(lot.truckFieldArrivalReal, lot.truckFieldArrivalTheorical)
    return lastUsage != null && fieldArrival != null && lastUsage > fieldArrival
  },

  /**
   * Comparator ordering lots by factory arrival (real beats theoretical), lots without any
   * arrival time last.
   */
  compareByFactoryArrival: (a: Lot, b: Lot): number => {
    const aTime = realOrTheorical(a.truckFactoryArrivalReal, a.truckFactoryArrivalTheorical)
    const bTime = realOrTheorical(b.truckFactoryArrivalReal, b.truckFactoryArrivalTheorical)
    if (aTime != null && bTime != null) {
      return aTime - bTime
    }
    if (aTime != null) {
      return -1
    }
    if (bTime != null) {
      return 1
    }
    return 0
  },
} as const
